/* ------------------------------------------------------------------
 *  CourseCard — Édition "Éditorial Academy"
 *  Carte de cours alignée avec la charte AY Solutions Academy :
 *  Fraunces, hairlines, marine + accent doré, curtain reveal au hover.
 * ------------------------------------------------------------------ */
import { Link } from "react-router-dom"
import { Clock, Star, ArrowUpRight } from "lucide-react"
import { formatDuration, formatPrice } from "../../utils/formatters"

function levelLabel(level) {
  switch (level) {
    case "BEGINNER":     return "Débutant"
    case "INTERMEDIATE": return "Intermédiaire"
    case "ADVANCED":     return "Avancé"
    default:             return level || "Tous niveaux"
  }
}

export default function CourseCard({
  course,
  index = 0,
  clickable = true,
  showPrice = true,
}) {
  const href = `/courses/${course.id}`
  const isFree = course?.isFree || course?.price === "0" || !course?.price
  const hasOriginal =
    course.originalPrice &&
    parseFloat(course.originalPrice) > 0 &&
    parseFloat(course.originalPrice) > parseFloat(course.price || 0)

  const Wrapper = clickable ? Link : "div"
  const wrapperProps = clickable ? { to: href } : {}

  return (
    <Wrapper
      {...wrapperProps}
      style={{ transitionDelay: `${index * 60}ms` }}
      className="course-card group relative flex flex-col bg-white border border-[#D5DAE3] hover:border-[#203a8a] transition-all duration-500 overflow-hidden"
    >
      {/* ---- Visual ---- */}
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F1F3F8]">
        <img
          src={course.thumbnail || course.banner || "/images/banner.jpg"}
          alt={course.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-110"
          onError={(e) => {
            e.currentTarget.src = "/images/banner.jpg"
          }}
        />

        {/* Voile dégradé léger */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B2240]/55 via-transparent to-transparent" />

        {/* Gratuit (overlay coin gauche bas) */}
        {showPrice && isFree && (
          <span className="absolute bottom-3 left-3 bg-[#E8C547] text-[#203a8a] text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 font-semibold">
            Gratuit
          </span>
        )}

        {/* Curtain reveal au hover */}
        <div className="absolute inset-0 bg-[#203a8a]/0 group-hover:bg-[#203a8a]/85 transition-colors duration-500 flex items-center justify-center">
          <span className="opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.3em]">
            Voir le module
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>

      {/* ---- Body ---- */}
      <div className="flex flex-col flex-1 p-5">
        {/* Eyebrow row : catégorie · note */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-medium truncate">
            {course.category?.translatedName ||
              course.category?.name ||
              "Catégorie"}
          </span>
          <span className="flex items-center gap-1 text-[#1B2240] text-xs">
            <Star className="w-3.5 h-3.5 fill-[#E8C547] text-[#E8C547]" />
            <span className="font-medium">{course.rating && course.rating > 0 ? course.rating : 4.5}</span>
          </span>
        </div>

        {/* Titre Fraunces */}
        <h3 className="font-display text-xl leading-tight text-[#203a8a] line-clamp-2">
          <span className="course-card-title">{course.title}</span>
        </h3>

        {/* Hairline */}
        <div className="my-4 h-px bg-[#D5DAE3]" />

        {/* Footer : durée + prix */}
        <div className="mt-auto flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] uppercase tracking-[0.2em]">
            <Clock className="w-3.5 h-3.5" />
            {formatDuration(course.duration, "seconds")}
          </span>

          {showPrice && !isFree && (
            <div className="flex items-baseline gap-2 text-right">
              {hasOriginal && (
                <span className="text-[11px] text-[#9CA3AF] line-through">
                  {formatPrice(course.originalPrice, course.currency || "XOF")}
                </span>
              )}
              <span className="font-display text-lg text-[#203a8a]">
                {formatPrice(course.price, course.currency || "XOF")}
              </span>
            </div>
          )}

          {showPrice && isFree && (
            <span className="font-display text-lg text-[#203a8a]">Gratuit</span>
          )}

          {!showPrice && (
            <ArrowUpRight className="w-4 h-4 text-[#203a8a] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          )}
        </div>
      </div>
    </Wrapper>
  )
}
